import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  CheckSquare,
  Boxes,
  Users,
  ShieldAlert,
  Wrench,
  CalendarRange,
  BarChart3,
  Activity,
  Settings,
  Bell,
  Search,
  LogOut,
  Globe2,
  ChevronRight,
  Menu,
  X as XIcon,
  Car,
  FileText,
  Target,
  AlertTriangle,
  Hammer,
  MessageSquare,
  HelpCircle,
  Map,
  Megaphone,
  Monitor,
  Ship,
} from "lucide-react";
import BranchScopeSelector from "./BranchScopeSelector.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { useAuth } from "../lib/auth.jsx";
import { api } from "../lib/api.js";
import { fmtRelative } from "../lib/format.js";
import CompleteTaskDialog from "./CompleteTaskDialog.jsx";
import { visibleSections } from "../config/navigation.js";
import OnboardingTour from "./OnboardingTour.jsx";
import UnderConstructionBlock from "./UnderConstructionBlock.jsx";
import { isUnderConstruction } from "../config/underConstruction.js";
import ContextualHelpButton from "./ContextualHelpButton.jsx";

const WELCOME_TOUR_STEPS = [
  {
    target: "[data-testid=sidebar]",
    title: "Os seus módulos",
    body: "A barra lateral agrupa tudo o que precisa de usar: Logística, Comercial, Operacional (SOM/BPS/Peças), CRM, Analítica e Admin.",
    placement: "right",
  },
  {
    target: "[data-testid=nav-announcements]",
    title: "Mural de Anúncios",
    body: "A plataforma arranca sempre aqui. É o canal oficial do distribuidor Maputo — anúncios, procedimentos e alertas para toda a equipa.",
    placement: "right",
  },
  {
    target: "[data-testid=nav-logistics-vehicles]",
    title: "Logística de Viaturas",
    body: "Grupo próprio no topo. Controla cada viatura do porto até à transferência para VR. VIN é a chave única em todo o sistema. Submenus: Viaturas, Alertas e Configurações.",
    placement: "right",
  },
  {
    target: "[data-testid=nav-branch-map]",
    title: "Mapa das filiais",
    body: "Visão executiva com todas as 11 filiais de Ronil. Veja a carga aberta por filial em tempo real.",
    placement: "right",
  },
  {
    target: "[data-testid=global-search-input]",
    title: "Pesquisa global",
    body: "Procure SOMs, PCPs, VINs, matrículas, clientes ou qualquer viatura em logística — resultados instantâneos em todas as áreas.",
    placement: "bottom",
  },
  {
    target: "[data-testid=help-button]",
    title: "Centro de Ajuda",
    body: "Precisa de ajuda com qualquer fluxo? Clique aqui para abrir o manual pesquisável, por módulo, com glossário e credenciais UAT.",
    placement: "bottom",
  },
  {
    target: "[data-testid=user-profile-link]",
    title: "O seu perfil",
    body: "Veja o seu perfil, defina preferências e termine sessão aqui. Boa sorte na jornada Ronil Command Center!",
    placement: "bottom",
  },
];

const NAV_ICONS = {
  LayoutDashboard, ClipboardList, CheckSquare, Boxes, Users, ShieldAlert, Wrench,
  CalendarRange, BarChart3, Activity, Settings, Car, FileText, Target,
  AlertTriangle, Hammer, MessageSquare, Map, Megaphone, Monitor, Ship, Bell,
};

const NOTIF_TASK_MANAGER_ROLES = ["admin", "national_aftersales_manager", "department_manager"];

function NavItem({ to, icon: Icon, label, testid, badge }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-heading font-medium transition-all duration-150 ${
          isActive
            ? "bg-white/10 text-white shadow-[inset_2px_0_0_0_#C9A96E]"
            : "text-slate-300 hover:text-white hover:bg-white/5"
        }`
      }
    >
      <Icon className="w-[18px] h-[18px] shrink-0 transition-transform group-hover:scale-105" strokeWidth={1.75} />
      <span className="flex-1 truncate">{label}</span>
      {badge != null && badge > 0 && (
        <span className="text-[10px] font-bold bg-ronil-sand text-ronil-navy-900 px-1.5 py-0.5 rounded-sm">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const blocked = isUnderConstruction(location.pathname);
  const [notifications, setNotifications] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [announcementToastShown, setAnnouncementToastShown] = useState(false);
  const [quickTask, setQuickTask] = useState(null); // { task, mode } for inline complete from bell
  const [quickTaskLoading, setQuickTaskLoading] = useState(null); // notification id while loading
  const [quickTaskError, setQuickTaskError] = useState("");
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  // Keyboard shortcut: Ctrl/⌘+K or "/" focuses the global search.
  // Esc blurs/clears it. Ignored when the user is already typing in an input.
  useEffect(() => {
    const onKey = (e) => {
      const typingInField =
        document.activeElement &&
        (/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName) ||
          document.activeElement.isContentEditable);
      const isCtrlK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";
      const isSlash = e.key === "/" && !typingInField;
      if (isCtrlK || isSlash) {
        e.preventDefault();
        if (searchRef.current) {
          searchRef.current.focus();
          searchRef.current.select();
        }
      }
      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        searchRef.current.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const [searchResults, setSearchResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const unread = notifications.filter((n) => !n.read).length;
  const can = (m) => !user?.effective_modules || user.effective_modules.includes(m);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifications(data);
      } catch {}
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Poll announcement unread count (every 60s). Show one-time toast at login.
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await api.get("/announcements/unread-count");
        const n = Number(data?.unread || 0);
        setUnreadAnnouncements(n);
        if (!announcementToastShown && n > 0) {
          setAnnouncementToastShown(true);
          try {
            const { toast } = await import("sonner");
            toast(`${n} ${n === 1 ? "anúncio novo" : "anúncios novos"}`, {
              description: "Visita o Mural do Distribuidor para ler.",
              action: { label: "Abrir", onClick: () => navigate("/anuncios") },
            });
          } catch {}
        }
      } catch {}
    };
    load();
    const onRead = () => load();
    window.addEventListener("rcc:announcement-read", onRead);
    const interval = setInterval(load, 60000);
    return () => { clearInterval(interval); window.removeEventListener("rcc:announcement-read", onRead); };
  }, [user, announcementToastShown, navigate]);

  useEffect(() => {
    if (!query || query.length < 3) {
      setSearchResults(null);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`);
        setSearchResults(data);
        setShowResults(true);
      } catch {}
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const isManager = user && ["admin", "national_aftersales_manager"].includes(user.role);

  const handleNotificationClick = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setShowNotif(false);
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const isTaskNotif = (n) => n?.record_type === "task" && !!n?.record_id;

  const openQuickTask = async (n) => {
    setQuickTaskError("");
    setQuickTaskLoading(n.id);
    try {
      const { data: task } = await api.get(`/tasks/${n.record_id}`);
      const isTaskManager = NOTIF_TASK_MANAGER_ROLES.includes(user?.role);
      const isReadOnly = user?.role === "read_only_viewer";
      const canAct = !isReadOnly && (isTaskManager || task.responsible_user_id === user?.id);
      if (!canAct) {
        setQuickTaskError("Sem permissão para alterar esta tarefa.");
        return;
      }
      if (task.status === "Cancelada") {
        setQuickTaskError("Tarefa cancelada não pode ser alterada.");
        return;
      }
      const mode = task.status === "Concluída" ? "reopen" : "complete";
      setQuickTask({ task, mode, notifId: n.id });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setQuickTaskError("Sem permissão para alterar esta tarefa.");
      } else if (status === 404) {
        setQuickTaskError("Tarefa não encontrada.");
      } else {
        setQuickTaskError("Não foi possível carregar a tarefa.");
      }
    } finally {
      setQuickTaskLoading(null);
    }
  };

  const onQuickTaskDone = async () => {
    if (quickTask?.notifId) {
      await api.post(`/notifications/${quickTask.notifId}/read`).catch(() => {});
      setNotifications((prev) => prev.map((x) => (x.id === quickTask.notifId ? { ...x, read: true } : x)));
    }
    setQuickTask(null);
    setShowNotif(false);
  };

  return (
    <div className="flex h-screen w-full bg-[#F4F6F8]">
      {/* First-login guided tour */}
      <OnboardingTour tourKey="welcome-v1" steps={WELCOME_TOUR_STEPS} />
      {/* Mobile overlay */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-overlay" />
      )}
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`w-60 ${mobileNavOpen ? "flex fixed inset-y-0 left-0 z-50" : "hidden"} md:flex md:static md:z-auto flex-col bg-ronil-navy text-white shrink-0`}
        style={{
          backgroundImage:
            "linear-gradient(180deg, #0C1A33 0%, #1F3864 25%, #1F3864 100%)",
        }}
      >
        <div className="px-4 py-4 border-b border-white/10 flex items-center gap-2.5">
          <div className="w-9 h-9 bg-white text-ronil-navy font-display font-bold text-lg rounded-sm flex items-center justify-center shadow-sm">R</div>
          <div className="leading-tight">
            <div className="font-heading font-black text-[13px] tracking-[0.1em] text-white">RONIL</div>
            <div className="text-[9px] text-ronil-sand uppercase tracking-[0.25em] mt-0.5">Command Center</div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMobileNavOpen(false); }}
            className="md:hidden ml-auto p-1 text-slate-300 hover:text-white"
            data-testid="mobile-nav-close"
            aria-label="Fechar menu"
          >
            <XIcon className="w-4 h-4 pointer-events-none" />
          </button>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto" onClick={() => setMobileNavOpen(false)}>
          {visibleSections({ can, isManager }).map((section) => (
            <React.Fragment key={section.title}>
              <div className="pt-4 pb-1.5 px-3 text-[9px] uppercase tracking-[0.25em] text-slate-400 font-heading font-semibold first:pt-1">
                {section.title}
              </div>
              {section.items.map((item) => {
                const Icon = NAV_ICONS[item.icon];
                const label = item.label?.startsWith("i18n:")
                  ? t(item.label.slice(5))
                  : item.label;
                const badge = item.to === "/anuncios" ? unreadAnnouncements : undefined;
                return (
                  <NavItem key={item.to} to={item.to} icon={Icon} label={label} testid={item.testid} badge={badge} />
                );
              })}
            </React.Fragment>
          ))}
        </nav>
        <div className="px-4 py-3 border-t border-white/10 text-[10px] text-slate-300">
          <div className="font-heading font-semibold tracking-wide text-white/90">
            BMW · Mazda · Hyundai · GWM
          </div>
          <div className="mt-0.5 text-slate-400">12 filiais · Moçambique</div>
          <div className="mt-2 text-[9px] uppercase tracking-[0.25em] text-ronil-sand/80">Desde 1946</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top nav */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-3 sm:px-5 sticky top-0 z-40 shrink-0 gap-3">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-1.5 hover:bg-slate-100 rounded-md text-slate-700"
            data-testid="mobile-nav-toggle"
            aria-label="Abrir menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="relative flex-1 max-w-xl">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={searchRef}
              data-testid="global-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => searchResults && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              placeholder={t("search_placeholder")}
              className="w-full bg-slate-50 border border-slate-200 rounded-md pl-9 pr-20 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ronil-navy/20 focus:border-ronil-navy focus:bg-white transition"
            />
            <kbd className="hidden sm:inline-flex items-center gap-0.5 absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-400 pointer-events-none select-none bg-white border border-slate-200 rounded px-1 py-0.5">
              Ctrl+K
            </kbd>
            {showResults && searchResults && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-96 overflow-y-auto z-50">
                {["service_cases", "parts_requests", "tasks", "classification_requests", "warranty_claims", "maintenance_plans", "vehicle_registrations", "customers", "fuel_requests", "vehicles"].every((k) => (searchResults[k] || []).length === 0) ? (
                  <div className="p-3 text-sm text-[#6C757D]">{t("no_records")}</div>
                ) : (
                  ["service_cases", "parts_requests", "tasks", "classification_requests", "warranty_claims", "maintenance_plans", "vehicle_registrations", "customers", "fuel_requests", "vehicles"].map((k) =>
                    (searchResults[k] || []).length > 0 ? (
                      <div key={k} className="border-b border-gray-100 last:border-0">
                        <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[#6C757D]">{searchResults.labels?.[k] || k.replaceAll("_", " ")}</div>
                        {searchResults[k].map((r) => (
                          <Link
                            key={r.id}
                            data-testid={`search-result-${r.id}`}
                            to={
                              k === "service_cases"
                                ? `/service-cases/${r.id}`
                                : k === "parts_requests"
                                ? `/parts-requests/${r.id}`
                                : k === "tasks"
                                ? `/tasks`
                                : k === "classification_requests"
                                ? `/classification-requests`
                                : k === "warranty_claims"
                                ? `/warranty-claims`
                                : k === "vehicle_registrations"
                                ? `/vehicle-registrations/${r.id}`
                                : k === "customers"
                                ? `/customers/${r.id}`
                                : k === "fuel_requests"
                                ? `/service-cases/${r.service_case_id}`
                                : k === "vehicles"
                                ? `/vehicles/${r.vin}`
                                : `/maintenance-plans`
                            }
                            onClick={() => setShowResults(false)}
                            className="block px-3 py-1.5 hover:bg-[#F4F6F8] text-sm flex items-center justify-between"
                          >
                            <span className="font-mono text-[#1F3864]">{r.number || r.vin}</span>
                            <span className="text-[#1A1A2E] truncate ml-3 flex-1">
                              {r.customer_name || r.title}
                            </span>
                            <span className="text-[#6C757D] text-xs ml-2">{r.vehicle_registration}</span>
                          </Link>
                        ))}
                      </div>
                    ) : null
                  )
                )}
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <BranchScopeSelector />
            <Link
              to="/ajuda"
              data-testid="help-button"
              className="p-2 hover:bg-slate-100 rounded-md text-slate-600 hover:text-ronil-navy transition-colors"
              title="Centro de Ajuda"
              aria-label="Centro de Ajuda"
            >
              <HelpCircle className="w-5 h-5" />
            </Link>
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "en" ? "pt" : "en")}
              className="flex items-center gap-1 text-xs text-[#1A1A2E] hover:text-[#1F3864]"
              title="Toggle language"
            >
              <Globe2 className="w-4 h-4" />
              <span className="uppercase font-mono">{lang}</span>
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                data-testid="notifications-bell"
                onClick={() => setShowNotif(!showNotif)}
                className="relative p-1.5 hover:bg-[#F4F6F8] rounded-sm"
              >
                <Bell className="w-5 h-5 text-[#1A1A2E]" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#DC3545] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </button>
              {showNotif && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-semibold text-sm">{t("notifications")}</span>
                    <button
                      data-testid="mark-all-read"
                      onClick={markAllRead}
                      className="text-xs text-[#1F3864] hover:underline"
                    >
                      {t("mark_all_read")}
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-sm text-[#6C757D] text-center">{t("no_notifications")}</div>
                    ) : (
                      notifications.slice(0, 10).map((n) => {
                        const taskish = isTaskNotif(n);
                        const loading = quickTaskLoading === n.id;
                        return (
                          <div
                            key={n.id}
                            data-testid={`notification-${n.id}`}
                            className={`w-full px-3 py-2 border-b border-gray-50 hover:bg-[#F4F6F8] ${
                              !n.read ? "bg-[#FFFBEB]" : ""
                            }`}
                          >
                            <button
                              onClick={() => handleNotificationClick(n)}
                              className="w-full text-left"
                              data-testid={`notification-open-${n.id}`}
                            >
                              <div className="text-sm font-medium text-[#1A1A2E] truncate">{n.title}</div>
                              <div className="text-xs text-[#6C757D] truncate">{n.body}</div>
                              <div className="text-[10px] text-[#94A3B8] mt-0.5">{fmtRelative(n.created_at)}</div>
                            </button>
                            {taskish && (
                              <div className="mt-1.5 flex items-center gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openQuickTask(n); }}
                                  disabled={loading}
                                  className="text-[11px] bg-[#28A745] hover:bg-[#1E7E34] text-white px-2 py-0.5 rounded-sm font-medium disabled:opacity-60"
                                  data-testid={`notification-quick-task-${n.id}`}
                                >
                                  {loading ? "…" : "Concluir / Reabrir tarefa"}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {quickTaskError && (
                    <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] text-xs border-t border-gray-100" data-testid="notification-quick-error">
                      {quickTaskError}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-slate-200">
              <Link
                to="/profile"
                className="text-right leading-tight hover:bg-slate-50 rounded-md px-2 py-1 transition-colors"
                data-testid="user-profile-link"
                title="Meu perfil"
              >
                <div className="text-sm font-heading font-semibold text-slate-900" data-testid="user-name">{user?.name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
                  {user?.role?.replaceAll("_", " ")}
                </div>
              </Link>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="p-2 hover:bg-slate-100 rounded-md text-slate-600 hover:text-ronil-navy transition-colors"
                title="Sair"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
            <button
              data-testid="logout-btn-mobile"
              onClick={logout}
              className="sm:hidden p-1.5 hover:bg-slate-100 rounded-md text-slate-700"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-ronil-light">
          {blocked ? <UnderConstructionBlock /> : children}
        </main>
      </div>
      {quickTask && (
        <CompleteTaskDialog
          task={quickTask.task}
          mode={quickTask.mode}
          onClose={() => setQuickTask(null)}
          onDone={onQuickTaskDone}
        />
      )}
      <ContextualHelpButton />
    </div>
  );
}
